import {getRandomColor} from "../../../utils/GetRandomColor";
import {Player} from "@lottiefiles/react-lottie-player";
import heyIAlreadyDidThat from "../../../assets/json/2.1-hey-i-already-did-that.json";
import IconText from "../../IconText";
import {faListCheck} from "@fortawesome/free-solid-svg-icons";

export default function HeyIAlreadyDidThat() {
    const lottieStyle = {
        border: `5px solid ${getRandomColor()}`,
    };
    
    return (
        <div className={"blog-body"}>
            <h1><IconText text={"2.1. Hey, I Already Did That!"} icon={faListCheck}/></h1>

            <h2>Question</h2>
            <div className={"blog-quote"}>
                <p>
                    Commander Lambda uses an automated algorithm to assign minions randomly to tasks, in order to
                    keep
                    minions on their toes. But you've noticed a flaw in the algorithm -- it eventually loops back on
                    itself,
                    so that instead of assigning new minions as it iterates, it gets stuck in a cycle of values so
                    that
                    the
                    same minions end up doing the same tasks over and over again. You think proving this to
                    Commander
                    Lambda
                    will help you make a case for your next promotion.
                </p>
                <p>
                    You have worked out that the algorithm has the following process:
                </p>
                <p>
                    1) Start with a random minion ID n, which is a nonnegative integer of length k in base b <br/>
                    2) Define x and y as integers of length k. x has the digits of n in descending order, and y has
                    the
                    digits of n in ascending order <br/>
                    3) Define z = x - y. Add leading zeros to z to maintain length k if necessary <br/>
                    4) Assign n = z to get the next minion ID, and go back to step 2 <br/>
                </p>
                <p>
                    For example, given minion ID n = 1211, k = 4, b = 10, then x = 2111, y = 1112 and z = 2111 -
                    1112 =
                    0999. Then the next minion ID will be n = 0999 and the algorithm iterates again: x = 9990, y =
                    0999
                    and
                    z = 9990 - 0999 = 8991, and so on.
                </p>
                <p>
                    Depending on the values of n, k (derived from n), and b, at some point the algorithm reaches a
                    cycle,
                    such as by reaching a constant value. For example, starting with n = 210022, k = 6, b = 3, the
                    algorithm
                    will reach the cycle of values [210111, 122221, 102212] and it will stay in this cycle no matter
                    how
                    many times it continues iterating. Starting with n = 1211, the routine will reach the integer
                    6174,
                    and
                    since 7641 - 1467 is 6174, it will stay as that value no matter how many times it iterates.
                </p>
                <p>
                    Given a minion ID as a string n representing a nonnegative integer of length k in base b, where
                    2 &lt;=
                    k
                    &lt;= 9 and 2 &lt;= b &lt;= 10, write a function solution(n, b) which returns the length of the
                    ending
                    cycle of
                    the algorithm above starting with n. For instance, in the example above, solution(210022, 3)
                    would
                    return 3, since iterating on 102212 would return to 210111 when done in base 3. If the algorithm
                    reaches a constant, such as 0, then the length is 1. </p>
                <p>
                    <b>Test cases</b>
                </p>
                <p>
                    Input: <br/>
                    solution.solution('1211', 10) <br/>
                    Output: <br/>
                    1 <br/><br/>

                    Input: <br/>
                    solution.solution('210022', 3) <br/>
                    Output: <br/>
                    3 <br/>
                </p>
            </div>

            <h2>Solution</h2>

            <p>
                My approach to this question is to create a list to store the values of z and check if the new value
                of
                z is
                in the list after each iteration. If it is, then we can return the length of the list minus the
                index of
                the
                new value of z. If it is not, then we can add the new value of z to the list and continue the
                iteration.
            </p>
            <p>
                To find the value of z, we can convert the string n to a list of integers, sort it in descending
                order
                to
                find x, sort it in ascending order to find y, and then find z by subtracting y from x. We can
                convert
                the
                list of integers back to a string and add leading zeros if necessary to maintain the length of k.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={heyIAlreadyDidThat} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>

            <p>
                Python's <code>int</code> function has a <code>base</code> parameter that can be used to convert a
                string to
                an
                integer, with the base parameter specifying the base of the number in the string.
            </p>

            <pre><code className="language-python">{`print(int('210111', 3)) # Prints 580`}</code></pre>
            <p>
                However, Python doesn't have a built-in function to convert an integer to a string with a specific
                base.
                We
                can create a
                function to do this by using the <code>divmod</code> function to find the quotient and remainder of
                the
                division.
            </p>
            <pre><code className="language-python">{`def to_base_b(n, b):
    if n == 0:
        return '0'
    numbers = []
    while n:
        n, r = divmod(n, b)
        numbers.append(str(r))
    return ''.join(reversed(numbers))`}</code></pre>

            <p>
                We know that 580 in base 3 is 210111. Let's test our function with this example.
            </p>

            <pre><code className="language-python">{`print(to_base_b(580, 3)) # Prints 210111`}</code></pre>

            <p>
                Now we can implement our algorithm to find the length of the ending cycle of the algorithm.
            </p>


            <h2>Code</h2>

            <pre><code className="language-python">{`def to_base_b(n, b):
    """
    Converts an integer n to a string in base b.
    """
    if n == 0:
        return '0'

    numbers = []

    while n:
        n, r = divmod(n, b)
        numbers.append(str(r))

    return ''.join(reversed(numbers))

def solution(n, b):
    list_of_z = []

    while True:
        # Converting n to a string
        n = str(n)

        # Creating x by sorting n in descending order
        x = ''.join(sorted(n, reverse=True))

        # Creating y by sorting n in ascending order
        y = ''.join(sorted(n))

        # Converting x and y to base b
        x = int(x, b)
        y = int(y, b)

        # Calculating z
        z = x - y

        # Converting z to base b
        z = to_base_b(z, b)

        # Adding leading zeros to z to maintain length k if necessary
        while len(z) < len(n):
            z = '0' + z

        # Checking if z is in the list of z's
        if z in list_of_z:
            return len(list_of_z) - list_of_z.index(z)
        else:
            list_of_z.append(z)
            n = z`}</code></pre>
        </div>
    );
}
    